import { useState, useRef } from 'react';
import styled from 'styled-components';
import { BrandSource, BrandEnrichmentResult } from '@agency-smm/shared';
import { Button } from '../Button';
import { Input, TextArea, ErrorMessage } from '../Input';
import { useBrandContext } from './brandShared';
import { useBrandSources } from '../../hooks/brand/useBrandSources';
import { EnrichReviewModal } from './EnrichReviewModal';
import { AiLoadingOverlay } from '../AiLoadingOverlay';

// Tab "Fuentes": knowledge base estilo NotebookLLM. Sumás webs / redes / notas,
// las prendés o apagás, y "Enriquecer perfil" sintetiza las activas en un diff
// que revisás. Las fuentes se persisten solas (aparte del Save del form).

const Wrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg};
`;

const Card = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.surface};
`;

const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin-bottom: 4px;
`;

const Desc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  line-height: 1.5;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const AddRow = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.sm};
  align-items: stretch;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
  }
`;

const LinkBtn = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  padding: 0;
  margin-top: ${({ theme }) => theme.spacing.sm};

  &:hover { text-decoration: underline; }
`;

const NoteBox = styled.div`
  margin-top: ${({ theme }) => theme.spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const ListHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Count = styled.span`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const Row = styled.div<{ $on: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => theme.spacing.sm} 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  opacity: ${({ $on }) => ($on ? 1 : 0.55)};
`;

const Toggle = styled.input`
  margin-top: 4px;
  width: 18px;
  height: 18px;
  cursor: pointer;
  flex-shrink: 0;
`;

const Icon = styled.span`
  font-size: 18px;
  line-height: 1.4;
  flex-shrink: 0;
`;

const Meta = styled.div`
  flex: 1;
  min-width: 0;
`;

const LabelRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const SrcLabel = styled.span`
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
`;

const Badge = styled.span<{ $tone: 'ok' | 'warn' | 'muted' }>`
  font-size: 10px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  padding: 2px 6px;
  border-radius: 4px;
  color: white;
  background: ${({ theme, $tone }) =>
    $tone === 'ok' ? theme.colors.success : $tone === 'warn' ? '#D97706' : theme.colors.textMuted};
`;

const Sub = styled.div`
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.colors.textMuted};
  margin-top: 2px;
  word-break: break-all;
`;

const Del = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 15px;
  color: ${({ theme }) => theme.colors.textMuted};
  flex-shrink: 0;
  &:hover { color: ${({ theme }) => theme.colors.error}; }
`;

const EmptyState = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textMuted};
  text-align: center;
  padding: ${({ theme }) => theme.spacing.lg} 0;
`;

function sourceIcon(s: BrandSource): string {
  if (s.kind === 'text') return '📝';
  if (s.kind === 'file') return '📄';
  switch (s.platform) {
    case 'instagram': return '📷';
    case 'facebook': return '📘';
    case 'linkedin': return '💼';
    case 'x': return '✖️';
    case 'tiktok': return '🎵';
    case 'youtube': return '▶️';
    case 'pinterest': return '📌';
    default: return '🌐';
  }
}

function statusBadge(s: BrandSource): { tone: 'ok' | 'warn' | 'muted'; text: string } {
  if (s.status === 'ready') return { tone: 'ok', text: 'Ready' };
  if (s.status === 'blocked') return { tone: 'warn', text: 'Blocked' };
  return { tone: 'muted', text: 'No text' };
}

export function BrandTabSources() {
  const { workspaceId, form } = useBrandContext();
  const srcs = useBrandSources(workspaceId);

  const [url, setUrl] = useState('');
  const [showNote, setShowNote] = useState(false);
  const [note, setNote] = useState('');
  const [pasteFor, setPasteFor] = useState<string | null>(null);
  const [pasteVal, setPasteVal] = useState('');
  const [reviewResult, setReviewResult] = useState<BrandEnrichmentResult | null>(null);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [isEnriching, setIsEnriching] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const onFilePicked = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // permite re-subir el mismo archivo
    if (!file) return;
    try { await srcs.addFile(file); } catch { /* error en hook */ }
  };

  const activeCount = srcs.sources.filter((s) => s.enabled).length;

  const addUrl = async () => {
    const v = url.trim();
    if (!v) return;
    try { await srcs.add({ kind: 'url', url: v }); setUrl(''); } catch { /* error en hook */ }
  };

  const addNote = async () => {
    const v = note.trim();
    if (!v) return;
    try { await srcs.add({ kind: 'text', text: v }); setNote(''); setShowNote(false); } catch { /* error en hook */ }
  };

  const savePaste = async (id: string) => {
    const v = pasteVal.trim();
    if (!v) return;
    await srcs.pasteText(id, v);
    setPasteFor(null);
    setPasteVal('');
  };

  const runEnrich = async () => {
    setIsEnriching(true);
    try {
      const result = await srcs.enrich();
      if (result) {
        setReviewResult(result);
        setReviewOpen(true);
      }
    } finally {
      setIsEnriching(false);
    }
  };

  const onApplied = (count: number) => {
    if (count > 0) {
      form.setSuccess(`✨ ${count} field${count === 1 ? '' : 's'} enriched. Review and save.`);
      setTimeout(() => form.setSuccess(''), 4000);
    }
  };

  return (
    <Wrap>
      {/* Pantalla de carga animada mientras la IA sintetiza las fuentes. */}
      <AiLoadingOverlay
        open={isEnriching}
        kicker="AgencySMM"
        title="Enriching your brand…"
        subtitle="Reading your active sources and proposing changes — nothing is saved until you review."
        steps={[
          'Reading active sources',
          'Cross-referencing your profile',
          'Synthesizing suggestions',
          'Preparing your changes',
        ]}
      />

      {/* Alta de fuentes */}
      <Card>
        <Title>Brand sources</Title>
        <Desc>
          Add websites, social profiles, files (PDF, DOCX, XLSX, TXT) and notes. The AI reads them to{' '}
          <strong>enrich</strong> your profile without replacing what you already have — you review and accept each
          change. Social networks sometimes block automatic reading: if that happens, paste the text manually.
        </Desc>
        <>
            <AddRow>
              <Input
                type="url"
                placeholder="https://your-site.com  ·  or an Instagram / LinkedIn / Facebook profile…"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void addUrl(); } }}
                disabled={srcs.busy}
                style={{ flex: 1 }}
              />
              <Button onClick={() => void addUrl()} disabled={srcs.busy || !url.trim()}>
                {srcs.busy ? 'Reading…' : 'Add'}
              </Button>
            </AddRow>
            {showNote ? (
              <NoteBox>
                <TextArea
                  rows={4}
                  placeholder="Paste a brief, a transcript, existing copy, brand notes…"
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <Button onClick={() => void addNote()} disabled={srcs.busy || !note.trim()}>Add note</Button>
                  <Button $variant="outline" onClick={() => { setShowNote(false); setNote(''); }}>Cancel</Button>
                </div>
              </NoteBox>
            ) : (
              <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
                <LinkBtn type="button" onClick={() => setShowNote(true)}>+ Paste text / note</LinkBtn>
                <LinkBtn type="button" onClick={() => fileInputRef.current?.click()} disabled={srcs.busy}>
                  {srcs.busy ? 'Reading file…' : '📄 Upload file (PDF, DOCX, XLSX, TXT)'}
                </LinkBtn>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.docx,.xlsx,.txt,.csv,.md"
              style={{ display: 'none' }}
              onChange={onFilePicked}
            />
            {srcs.error && <ErrorMessage style={{ display: 'block', marginTop: 12 }}>{srcs.error}</ErrorMessage>}
        </>
      </Card>

      {/* Lista + enriquecer */}
      <Card>
        <ListHeader>
          <Count>
            {srcs.sources.length} source{srcs.sources.length === 1 ? '' : 's'} · {activeCount} active
          </Count>
          <Button onClick={() => void runEnrich()} disabled={srcs.busy || activeCount === 0}>
            {srcs.busy ? 'Analyzing…' : '✨ Enrich profile'}
          </Button>
        </ListHeader>

        {srcs.isLoading ? (
          <EmptyState>Loading sources…</EmptyState>
        ) : srcs.sources.length === 0 ? (
          <EmptyState>No sources yet. Add your website or a note to get started.</EmptyState>
        ) : (
          srcs.sources.map((s) => {
            const badge = statusBadge(s);
            const canPaste = s.status !== 'ready';
            return (
              <Row key={s.id} $on={s.enabled}>
                <Toggle
                  type="checkbox"
                  checked={s.enabled}
                  title={s.enabled ? 'Active for enrichment' : 'Off'}
                  onChange={(e) => void srcs.setEnabled(s.id, e.target.checked)}
                />
                <Icon aria-hidden="true">{sourceIcon(s)}</Icon>
                <Meta>
                  <LabelRow>
                    <SrcLabel>{s.label}</SrcLabel>
                    <Badge $tone={badge.tone}>{badge.text}</Badge>
                  </LabelRow>
                  <Sub>
                    {s.url ? s.url : s.kind === 'file' ? 'Uploaded file' : 'Pasted note'} · {s.contentChars ?? 0} chars
                  </Sub>
                  {canPaste && pasteFor !== s.id && (
                    <LinkBtn type="button" onClick={() => { setPasteFor(s.id); setPasteVal(''); }}>
                      Paste text manually
                    </LinkBtn>
                  )}
                  {pasteFor === s.id && (
                    <NoteBox>
                      <TextArea
                        rows={4}
                        placeholder="Paste this source's content (profile text, description, posts…)"
                        value={pasteVal}
                        onChange={(e) => setPasteVal(e.target.value)}
                      />
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button onClick={() => void savePaste(s.id)} disabled={srcs.busy || !pasteVal.trim()}>Save text</Button>
                        <Button $variant="outline" onClick={() => { setPasteFor(null); setPasteVal(''); }}>Cancel</Button>
                      </div>
                    </NoteBox>
                  )}
                </Meta>
                <Del type="button" title="Delete source" onClick={() => void srcs.remove(s.id)}>🗑</Del>
              </Row>
            );
          })
        )}
      </Card>

      <EnrichReviewModal
        isOpen={reviewOpen}
        result={reviewResult}
        form={form}
        onClose={() => setReviewOpen(false)}
        onApplied={onApplied}
      />
    </Wrap>
  );
}
